import { produce } from 'immer';
import dayjs from 'dayjs';
import api from '../api';
import { useRequest, useRequestImmer, useRequestImmerWithInfiniteList } from '../api/hooks';
import { ID } from '../global';
import { assertUnreachable } from '@/utils/common';
import { Appointment } from './appointment';
import { Card } from './card';
import { Page } from './common';
import { PaymentChannel } from './payment';
import { GOODS_TYPE, SKU, SKUSpecification, SKUWrapper } from './sku';
import { Staff } from './staff';
import { TransactionLog } from './transaction';
import { User, UserFullInfo } from './user';

export const OrderEndpoint = `/order`;

export const isServiceSKUWrapper = (skuWrapper: SKUWrapper) => {
  return skuWrapper.goods.type === GOODS_TYPE.SERVICE;
};

export const isServiceSKUSpecification = (skuSpecification: SKUSpecification) => {
  return skuSpecification.goodsType === GOODS_TYPE.SERVICE;
};
export const isServiceOrderSKU = (orderSku: OrderSku) => {
  return orderSku.goodsType === GOODS_TYPE.SERVICE;
};

export interface PosOrderConfig {
  // 是否打印标签
  printLabel: boolean;
  printLabelManual: boolean;
  printLabelIndex: boolean;
  printLabelGoodsName: boolean;
  printLabelOrderQr: boolean;
  printLabelPrice: boolean;
  printLabelSpecificationName: boolean;
  printLabelTitleEnabled: boolean;
  printLabelTitle: string;
  printLabelDateTime: boolean;
}

export const expressThirdPartyMap = {
  dada: '达达快送',
  eleme: '蜂鸟配送',
  meituan: '美团配送',
  shansong: '闪送',
  SF: '顺丰速运',
  YD: '韵达速递',
  YTO: '圆通速递',
  ZTO: '中通快递',
  EMS: 'EMS',
  DBL: '德邦快递',
  FAST: '快捷快递',
  ZJS: '宅急送',
  DHL: 'DHL',
  TNT: 'TNT快递',
  UPS: 'UPS',
};
export interface OrderDeliveryTypeConfig {
  on: boolean;
  title: string;
}

export enum OrderDeliveryType {
  IN_STORE_SERVICE = 1,
  IN_STORE_GOODS = 2,
  IN_STORE_SERVICE_AND_GOODS = 3,

  // 无需交付或虚拟物品(充值、预付卡等)
  INVALID = -1,
}

export const getDeliveryTypeDesc = (order: Order) => {
  switch (order.deliveryType) {
    case OrderDeliveryType.IN_STORE_SERVICE:
      return '到店服务';
    case OrderDeliveryType.IN_STORE_GOODS:
      return '店内购物';
    case OrderDeliveryType.IN_STORE_SERVICE_AND_GOODS:
      return '到店服务与购物';
    case OrderDeliveryType.INVALID:
      return 'N/A';
    default:
      return assertUnreachable(order.deliveryType);
  }
};

/**
 * 复合 DeliveryType 中是否包含某个特定的 DeliveryType
 * @param orderDeliveryType
 * @param hasType
 * @returns
 */
export const hasDeliveryType = (orderDeliveryType: OrderDeliveryType, hasType: OrderDeliveryType) => {
  return (orderDeliveryType & hasType) == hasType;
};

export enum OrderShipMethod {
  Self = 1,
  ThirdParty = 2,
}

export const getShipMethodDesc = (method: number) => {
  if (method === OrderShipMethod.Self) {
    return '门店送货';
  } else if (method === OrderShipMethod.ThirdParty) {
    return '快递配送';
  }
  return 'N/A';
};

export enum OrderType {
  // 直接支付
  Pay = 0,

  // 普通下单
  Normal = 1,

  // 秒杀订单
  SecKill = 2,

  // 充值
  Recharge = 4,

  // 购卡
  Card = 5,

  // 美团团购
  GROUPON_MT = 6,

  // 抖音团购
  GROUPON_DY = 7,
}

export const orderTypeDescMap = {
  [OrderType.Pay]: '直接支付',
  [OrderType.Normal]: '普通订单',
  [OrderType.SecKill]: '促销订单',
  [OrderType.Recharge]: '充值订单',
  [OrderType.Card]: '购卡订单',
  [OrderType.GROUPON_MT]: '美团团购',
  [OrderType.GROUPON_DY]: '抖音团购',
};

export enum OrderChannel {
  // 门店
  Store = 0,

  // 小程序
  Weapp = 1,
}

export const OrderStatusMap = {
  UnPaid: {
    type: 101,
    desc: '待付款',
  },
  UserCanceled: {
    type: 102,
    desc: '用户取消',
  },
  SystemCanceled: {
    type: 103,
    desc: '系统取消',
  },
  Paid: {
    type: 201,
    desc: '已付款',
  },
  Refunding: {
    type: 202,
    desc: '订单取消，退款中',
  },
  Refunded: {
    type: 203,
    desc: '已退款',
  },
  GrouponTimeUp: {
    type: 204,
    desc: '已超时团购',
  },
  Shipped: {
    type: 301,
    desc: '已发货',
  },
  Progressing: {
    type: 350,
    desc: '服务中',
  },
  Complete: {
    type: 401,
    desc: '已完成',
  },
  SystemComplete: {
    type: 402,
    desc: '已自动完成',
  },
};

export interface OrderSku {
  goodsId: null;
  goodsName: string;
  goodsSn: string;
  goodsDuration: number;
  goodsPicUrl: string;
  goodsType: GOODS_TYPE;
  type: GOODS_TYPE;
  addTime: string;
  updateTime: string;
  comment: number;
  cost: number;
  id: number;
  number: number;
  orderId: number;
  picUrl: string;
  price: number;
  productId: number;
  specificationId: number;
  specificationName: string;
  deliveryType: OrderDeliveryType;
}

export interface OrderDetail {
  order: Order;
  skuList: OrderSku[];
  user: User;
  appointment?: Appointment;
  appointmentSkuList?: SKU[];
  transactionList?: TransactionLog[];
}

export interface Order {
  id: number;
  userId: number;
  orderSn: string;
  orderType: OrderType;
  channel: OrderChannel;
  /**运费*/
  freightPrice?: number;

  /**优惠券抵扣*/
  couponPrice?: number;
  couponId?: number;
  couponName?: string;

  /**商品总价*/
  goodsPrice?: number;
  /**订单总价*/
  orderPrice?: number;

  /**预付卡抵扣**/
  cardPrice?: number;

  /**积分抵现**/
  pointNum?: number;
  pointPrice?: number;

  /**余额支付金额 */
  balancePrice: number;

  /**特殊减免金额*/
  waiverAmount: number;

  /**实付金额*/
  actualPrice: number;

  orderStatusText: string;
  orderStatus: number;
  /**下单时间*/
  addTime: string;
  /**微信交易单号*/
  payId: string;
  /**支付时间*/
  payTime: string;

  endTime: string;
  serviceStartTime: string;
  serviceEndTime: string;

  /**支付渠道*/
  payChannel: PaymentChannel;
  deliveryType: OrderDeliveryType;
  writeOffCode: string;
  message: string;
  deliveryTypeDesc: string;
  receiverName: string;
  receiverTel: string;
  receiverAddr: string;
  receiverLatitudeGcj02: string;
  receiverLongitudeGcj02: string;

  shipChannel: string;
  shipMethod: number;
  shipSn: string;
  shipTime: string;

  refundAmount: number;
  refundChannel: PaymentChannel;
  refundId: string;
  refundMemo: string;
  refundTime: string;

  cashier: string;
  operator: string;

  handleOption: {
    aftersale: boolean;
    cancel: boolean;
    comment: boolean;
    confirm: boolean;
    delete: boolean;
    pay: boolean;
    rebuy: boolean;
    refund: boolean;
  };
  skuList: SKU[];
}

/**收银台创建的预订单 */
export type PreOrder = {
  // 未 push 之前没有 key
  key?: string;
  // 购物单位
  specifications?: SKUSpecification[];
  // 用户与用户关联信息(积分、优惠券等)
  cards?: Card[];
  userFullInfo?: UserFullInfo;
  /**订单号 (小程序渠道购买的服务存在订单号) */
  orderSn?: string;
  /**创建开始时间 */
  addTime?: string;

  /**关联的预约记录 */
  appointment?: Appointment;

  operator?: Staff;
};

export const OrderOperationHelper = {
  /**
   * 能否退款
   * @param order
   */
  canOrderRefund: (order: Order) => {
    if (!order) return false;
    const orderStatus = order.orderStatus;
    return (
      orderStatus == OrderStatusMap.Paid.type ||
      orderStatus == OrderStatusMap.Shipped.type ||
      orderStatus == OrderStatusMap.Complete.type ||
      orderStatus == OrderStatusMap.SystemComplete.type
    );
  },

  canStart: (order: Order) => {
    if (!order) return false;
    const orderStatus = order.orderStatus;
    return orderStatus == OrderStatusMap.Paid.type;
  },

  canComplete: (order: Order) => {
    if (!order) return false;
    const orderStatus = order.orderStatus;
    return orderStatus == OrderStatusMap.Progressing.type;
  },
};

/**
 * 订单退款
 * @param order
 */
export const refundOrder = (order: Order) => {
  return api.post<Order>({
    url: `${OrderEndpoint}/refund`,
    data: {
      orderId: order.id,
    },
  });
};

export const requestUserOrderList = () =>
  useRequestImmer<{ list: Order[]; page: Page }>(
    {
      method: 'get',
    },
    {
      manual: true,
    }
  );

export const requestInfiniteUserOrderList = () =>
  useRequestImmerWithInfiniteList<{ list: Order[]; page: Page }>(
    {
      method: 'get',
    },
    {
      manual: true,
    }
  );

export const updateOperatorReq = async (data: { orderSn: ID; operatorUsername: ID }) => {
  return api.post<Order>({
    url: `${OrderEndpoint}/operator`,
    data,
  });
};

/**
 * 设置订单的美容师
 * @param preOrder
 * @param operator
 * @returns
 */
export const setOperatorForPreOrder = async (preOrder: PreOrder, operator: Staff) => {
  const order = produce(preOrder, draft => {
    draft.operator = operator;
  });

  // 如果是线上订单(有 orderSn) ... 把美容师信息发送到服务器
  if (preOrder.orderSn) {
    await updateOperatorReq({
      orderSn: preOrder.orderSn,
      operatorUsername: operator.username,
    });
  }
  return order;
};

export const useStartOrder = () => {
  const { send, ...rest } = useRequestImmer<void>(
    {
      url: `${OrderEndpoint}/startOrder`,
      method: 'post',
    },
    {
      manual: true,
    }
  );

  return {
    ...rest,
    send: ({ orderId, id }) =>
      send({
        data: {
          orderId,
          id,
        },
      }),
  };
};

export const useCompleteOrder = () => {
  const { send, ...rest } = useRequestImmer<Order>(
    {
      url: `${OrderEndpoint}/completeOrder`,
      method: 'post',
    },
    {
      manual: true,
    }
  );

  return {
    ...rest,
    send: ({ orderSn }) =>
      send({
        data: {
          orderSn,
        },
      }),
  };
};

export const AssignOrderToUserResult = {
  0: '无效订单信息',
  1: '无效客户信息',
  2: '超时',
  3: '订单用户已有归属',
  4: '订单用户和当前用户是同一人',
  200: '处理完成',
  201: '处理完成',
};

/**
 * 将指定的订单归属给指定客户
 * @param param0
 * @returns
 */
export const assignOrderToUser = async ({
  orderSn,
  wecomId,
  username,
}: {
  orderSn: string;
  wecomId?: string;
  username?: string;
}) => {
  return api.post<{ code: number; user: User; order: Order }>({
    url: `${OrderEndpoint}/assignOrderToUser/${orderSn}`,
    data: {
      wecomId,
      username,
    },
  });
};

/**
 * 预订单是否包含服务型商品
 * @param preOrder
 * @returns
 */
export const isServicePreOrder = (preOrder: PreOrder) => {
  if (preOrder && preOrder.specifications?.length) {
    for (const sku of preOrder.specifications) {
      if (isServiceSKUSpecification(sku)) {
        return true;
      }
    }
  }
  return false;
};

export interface StaffOrderPerformance {
  rechargeOrderAmount: number;
  serviceOrderAmount: number;
  byDateOrderCount: { [k: string]: number };
}
export const requestPerfByStaffMonth = () =>
  useRequest<StaffOrderPerformance>(
    {
      method: 'get',
      url: `${OrderEndpoint}/month/perf/{staffUserName}`,
    },
    {
      manual: true,
    }
  );
